import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import FileUploader from 'devextreme-react/file-uploader';
import Popup from 'devextreme-react/popup';
import SelectBox from 'devextreme-react/select-box';
import TextArea from 'devextreme-react/text-area';
import TextBox from 'devextreme-react/text-box';
import { FeedbackSuccessView } from './FeedbackSuccessView';
import { showErrorToast, showSuccessToast } from '../notifications/toast';
import { useResponsivePopupOptions } from '../shell/useResponsivePopup';
import type { BugSummary, SiteFeedbackApi, SiteSupportCopy } from './types';

const FALLBACK_SEVERITIES = ['Low', 'Medium', 'High', 'Critical'];
const MAX_MESSAGE = 4000;
const MAX_FILES = 5;

export interface ReportBugPopupProps {
  visible: boolean;
  onClose: () => void;
  api: SiteFeedbackApi;
  copy: SiteSupportCopy;
}

export function ReportBugPopup({ visible, onClose, api, copy }: ReportBugPopupProps) {
  const [subject, setSubject] = useState('');
  const [severity, setSeverity] = useState('Medium');
  const [severities, setSeverities] = useState<string[]>(FALLBACK_SEVERITIES);
  const [repeatIssue, setRepeatIssue] = useState(false);
  const [oldReferenceId, setOldReferenceId] = useState<string | undefined>();
  const [message, setMessage] = useState('');
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pickerVisible, setPickerVisible] = useState(false);
  const [bugSearch, setBugSearch] = useState('');
  const [bugOptions, setBugOptions] = useState<BugSummary[]>([]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    void api
      .listActiveMeta('BugSeverity')
      .then((rows) => {
        if (cancelled) return;
        const names = rows.map((r) => r.name);
        if (names.length === 0) return;
        setSeverities(names);
        setSeverity((prev) => (names.includes(prev) ? prev : names.includes('Medium') ? 'Medium' : names[0]));
      })
      .catch(() => {
        /* keep fallback list */
      });
    return () => {
      cancelled = true;
    };
  }, [api, visible]);

  const reset = useCallback(() => {
    setSubject('');
    setSeverity(severities.includes('Medium') ? 'Medium' : severities[0] ?? 'Medium');
    setRepeatIssue(false);
    setOldReferenceId(undefined);
    setMessage('');
    setAttachmentIds([]);
    setSuccess(false);
    setPickerVisible(false);
    setBugSearch('');
    setBugOptions([]);
  }, [severities]);

  const handleHide = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const loadBugOptions = useCallback(async () => {
    try {
      const items = await api.searchBugs(bugSearch || undefined);
      setBugOptions(items);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Could not load bugs.');
    }
  }, [api, bugSearch]);

  const onUpload = useCallback(
    async (file: File) => {
      if (attachmentIds.length >= MAX_FILES) {
        showErrorToast(`Maximum ${MAX_FILES} attachments.`);
        return;
      }
      setUploading(true);
      try {
        const result = await api.uploadAttachment(file);
        setAttachmentIds((prev) => [...prev, result.attachmentId]);
        showSuccessToast(`Attached ${result.fileName}`);
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : 'Upload failed.');
      } finally {
        setUploading(false);
      }
    },
    [api, attachmentIds.length]
  );

  const submit = useCallback(async () => {
    if (!subject.trim()) {
      showErrorToast('Subject is required.');
      return;
    }
    if (!message.trim()) {
      showErrorToast('Description is required.');
      return;
    }
    setSubmitting(true);
    try {
      await api.submitBug({
        subject: subject.trim(),
        message: message.trim(),
        severity,
        repeatIssue,
        oldReferenceId: repeatIssue ? oldReferenceId : undefined,
        attachmentIds: attachmentIds.length ? attachmentIds : undefined,
        pageUrl: window.location.href
      });
      setSuccess(true);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  }, [api, attachmentIds, message, oldReferenceId, repeatIssue, severity, subject]);

  const pickerItems = useMemo(
    () => bugOptions.map((b) => ({ id: b.bugId, text: `${b.subject} (${b.bugId})` })),
    [bugOptions]
  );

  const mainPopupOptions = useResponsivePopupOptions({ width: 640, height: 'auto' });
  const pickerPopupOptions = useResponsivePopupOptions({ width: 520, height: 'auto' });

  return (
    <>
      <Popup
        visible={visible}
        onHiding={handleHide}
        showTitle
        title={copy.bugReportTitle ?? 'Report Bug'}
        {...mainPopupOptions}
        maxHeight="90vh"
        dragEnabled={false}
        hideOnOutsideClick={!submitting}
      >
        {success ? (
          <FeedbackSuccessView kind="bug" />
        ) : (
          <div className="site-support-form">
            <label className="site-support-label">
              Subject <span className="site-support-required">*</span>
            </label>
            <TextBox
              value={subject}
              onValueChanged={(e) => setSubject(String(e.value ?? ''))}
              placeholder="Short summary (2-3 words)"
            />

            <label className="site-support-label">Severity</label>
            <SelectBox
              items={severities}
              value={severity}
              onValueChanged={(e) => setSeverity(String(e.value ?? severities[0] ?? 'Medium'))}
            />

            <div className="site-support-repeat-row">
              <CheckBox
                value={repeatIssue}
                onValueChanged={(e) => setRepeatIssue(Boolean(e.value))}
                text="Repeat issue"
              />
              {repeatIssue ? (
                <Button
                  text="Select existing bug"
                  stylingMode="outlined"
                  onClick={() => {
                    setPickerVisible(true);
                    void loadBugOptions();
                  }}
                />
              ) : null}
            </div>
            {repeatIssue && oldReferenceId ? (
              <div className="site-support-hint">Linked to bug: {oldReferenceId}</div>
            ) : null}

            <label className="site-support-label">
              Describe the bug <span className="site-support-required">*</span>
            </label>
            <TextArea
              value={message}
              height={140}
              maxLength={MAX_MESSAGE}
              onValueChanged={(e) => setMessage(String(e.value ?? ''))}
              placeholder="Describe what happened, steps to reproduce, and any error messages..."
            />
            <div className="site-support-char-count">
              {message.length}/{MAX_MESSAGE}
            </div>

            <label className="site-support-label">
              Attachments (images, docs, csv, json, xml, md, pdf, log, zip — max {MAX_FILES})
            </label>
            <FileUploader
              selectButtonText="browse"
              labelText="Drag & drop files here or"
              uploadMode="useButtons"
              multiple
              accept="image/*,.pdf,.csv,.json,.xml,.md,.txt,.log,.zip,.doc,.docx"
              disabled={uploading || attachmentIds.length >= MAX_FILES}
              onValueChanged={(e) => {
                const files = e.value as File[] | undefined;
                if (files?.length) void onUpload(files[files.length - 1]);
              }}
            />
            {attachmentIds.length > 0 ? (
              <div className="site-support-hint">{attachmentIds.length} file(s) attached</div>
            ) : null}

            <div className="site-support-form-actions">
              <Button text="Cancel" stylingMode="outlined" onClick={handleHide} disabled={submitting} />
              <Button
                text="Submit"
                type="default"
                onClick={() => void submit()}
                disabled={submitting || uploading}
              />
            </div>
          </div>
        )}
      </Popup>

      <Popup
        visible={pickerVisible}
        onHiding={() => setPickerVisible(false)}
        showTitle
        title="Select existing bug"
        {...pickerPopupOptions}
      >
        <div className="site-support-form">
          <TextBox
            value={bugSearch}
            onValueChanged={(e) => setBugSearch(String(e.value ?? ''))}
            placeholder="Search by subject or ID"
          />
          <Button text="Search" stylingMode="outlined" onClick={() => void loadBugOptions()} />
          <SelectBox
            items={pickerItems}
            displayExpr="text"
            valueExpr="id"
            value={oldReferenceId}
            onValueChanged={(e) => setOldReferenceId(e.value ? String(e.value) : undefined)}
            searchEnabled
          />
          <div className="site-support-form-actions">
            <Button text="Cancel" stylingMode="outlined" onClick={() => setPickerVisible(false)} />
            <Button
              text="Use selected"
              type="default"
              onClick={() => setPickerVisible(false)}
              disabled={!oldReferenceId}
            />
          </div>
        </div>
      </Popup>
    </>
  );
}
